<template>
	<Tooltip
		:heading="translate(`${resourceKey}.${translatableCurrency}`)"
		customWidth="18rem"
	>
		<template #element>
			<div
				class="main-resource"
				:class="[
					{
						'main-resource--not-enough':
							!hasEnoughResources || value < 0,
						'clickable-element':
							!hasEnoughResources && !statifiedStorageRequirement,
					},
					customClass,
				]"
				@click="handleResourceRepresentation"
			>
				<div class="main-resource-content">
					<div class="main-resource-content__icon">
						<AppMainIcon
							class="main-resource__icon"
							:iconName="currency"
							:iconSize="iconSize"
						/>
					</div>
					<span
						class="main-resource-content__value"
						:class="{ 'has-pulse': value < 0 }"
						v-html="formatNumber(animatedValueRef, true)"
					/>
				</div>
				<MainResourceBackground
					:type="
						hasEnoughResources || statifiedStorageRequirement
							? 'primary'
							: 'danger'
					"
				/>
				<AppNotification
					v-if="!hasEnoughResources && !statifiedStorageRequirement"
					class="main-resource__notification"
					:type="StatusTypes.WARNING"
					value="!"
				/>
			</div>
		</template>
		<template #tooltip>
			<div class="main-resource__tooltip">
				<div
					v-if="
						currency === ResourceType.FOOD_DIFF ||
						currency === ResourceType.POPULATION_DIFF ||
						currency === ResourceType.EVENTIUM
					"
					class="main-resource__tooltip-states"
				>
					<div
						v-if="consumption"
						class="main-resource__tooltip-state"
					>
						<div class="main-resource__tooltip-state-head">
							{{
								translate(
									`ui.${currency === ResourceType.FOOD_DIFF ? "consumption" : "employed"}`,
								)
							}}
						</div>
						<div
							class="main-resource__tooltip-state-value"
							:class="{
								'main-resource__tooltip-state-value-red':
									income < consumption,
							}"
						>
							{{ `${formatNumber(consumption)}` }}
						</div>
					</div>
					<div v-if="income" class="main-resource__tooltip-state">
						<div class="main-resource__tooltip-state-head">
							{{
								translate(
									`ui.${currency === ResourceType.FOOD_DIFF || currency === ResourceType.EVENTIUM ? "production" : "total"}`,
								)
							}}
						</div>
						<div class="main-resource__tooltip-state-value">
							{{ `${formatNumber(income)}` }}
						</div>
					</div>
					<div v-if="total" class="main-resource__tooltip-state">
						<div class="main-resource__tooltip-state-head">
							{{ translate("event.total_produced") }}
						</div>
						<div class="main-resource__tooltip-state-value">
							{{ `${formatNumber(total)}` }}
						</div>
					</div>
				</div>
				<div
					class="main-resource__tooltip-warning"
					v-if="consumption && income < consumption"
				>
					<div class="main-resource__tooltip-warning-head">
						{{ translate("resource.income_reduced") }}
					</div>
					<div>
						{{
							translate(
								`resource.${currency === ResourceType.FOOD_DIFF ? "food_warning_1" : "workforce_warning_1"}`,
							)
						}}
					</div>
					<div class="main-resource__tooltip-warning-bold">
						{{
							translate(
								`resource.${currency === ResourceType.FOOD_DIFF ? "food_warning_2" : "workforce_warning_2"}`,
							)
						}}
					</div>
				</div>
				<div
					v-if="currency === ResourceType.COINS"
					class="main-resource__tooltip-state"
				>
					<div class="main-resource__tooltip-state-value">
						{{ `${formatNumber(value)}` }}
					</div>
				</div>
				<span class="main-resource__tooltip-info">
					{{
						translate(
							`${resourceKey}.${translatableCurrency}_description`,
						)
					}}
				</span>
			</div>
		</template>
	</Tooltip>
</template>

<script lang="ts" setup>
import AppMainIcon from "@/components/AppMainIcon.vue";
import AppNotification from "@/components/AppNotification.vue";
import NotEnoughStoragePopup from "@/components/NotEnoughStoragePopup.vue";
import MainResourceBackground from "@/components/SVG/MainResourceBackground.vue";
import Tooltip from "@/components/Tooltip.vue";
import { NUMBER_ANIMATION_DURATION } from "@/config/constants";
import { useEventStore } from "@/event";
import { ResourceType } from "@/game/entities/config/resources";
import useEmitter, { EmitterEvents } from "@/hooks/useEmitter";
import {
	PopupDictionary,
	PopupType,
	usePopupStore,
} from "@/store/usePopupStore";
import useResourcesStorageStore from "@/store/useResourcesStorageStore";
import useResourcesStore from "@/store/useResourcesStore";
import useRuntimeStore from "@/store/useRuntimeStore";
import { StatusTypes } from "@/types/ui";
import { formatNumber } from "@/utils/format";
import { replacePlaceholder } from "@/utils/replacePlaceholder";
import { translate } from "@/utils/translate";
import { storeToRefs } from "pinia";
import {
	computed,
	markRaw,
	onBeforeUnmount,
	onMounted,
	ref,
	shallowRef,
	watch,
} from "vue";
// @ts-expect-error - TS7016
import { linear, useTween } from "vue-femtotween";

const emitter = markRaw(useEmitter());

const popupStore = usePopupStore();

const resourcesStore = useResourcesStore();
const resources = storeToRefs(resourcesStore);

const resourcesStorageStore = useResourcesStorageStore();
const resourcesStorage = storeToRefs(resourcesStorageStore);

const eventStore = useEventStore();
const event = storeToRefs(eventStore);

const runtimeStore = useRuntimeStore();
const { isSettlementWindowCollapsed } = storeToRefs(runtimeStore);

interface Props {
	currency: ResourceType;
	value: number;
	resourceCheck?: boolean;
	income?: number;
	consumption?: number;
	total?: number;
	customClass?: string;
	type?: string;
}

const {
	currency,
	value,
	resourceCheck = true,
	income = 0,
	consumption = 0,
	total = 0,
	customClass = "",
	type = "",
} = defineProps<Props>();

const topArchHeight = ref<number>(0);
const iconSizes = [
	14, 16, 20, 24, 26, 28, 32, 38, 48, 56, 72, 80, 90, 104, 120, 152, 184, 360,
];
const iconSize = ref<number>(32);

const currentStorageCapacity = ref<number>(resources[currency]?.value);
const maxStorageCapacity = ref<number>(resourcesStorage[currency]?.value);

const translatableCurrency = computed<string>((): string =>
	currency.replace("foodDiff", "food").replace("populationDiff", "workforce"),
);

const resourceKey = computed((): string => {
	if (currency === ResourceType.EVENTIUM) {
		return `event.${event.attributes.value.type}_event`;
	}
	return "resource";
});

// Throttled update function
const throttledUpdateStorageValues = (): void => {
	currentStorageCapacity.value = resources[currency]?.value;
	maxStorageCapacity.value = resourcesStorage[currency]?.value;
};

watch(
	(): number => resourcesStorage[currency]?.value,
	(): void => throttledUpdateStorageValues(),
	{ immediate: false },
);

const statifiedStorageRequirement = computed((): boolean => {
	if (typeof maxStorageCapacity.value === "undefined") return true;

	return value <= maxStorageCapacity.value;
});

const hasEnoughResources = ref<boolean>(true);

const updateHasEnoughResources = (): void => {
	if (!resourceCheck) {
		hasEnoughResources.value = true;
		return;
	}
	if (!currency) {
		hasEnoughResources.value = true;
		return;
	}

	hasEnoughResources.value =
		resourcesStore.getCurrencyValue(currency) >= value;
};

watch(
	(): number => resourcesStore.getCurrencyValue(currency),
	(): void => updateHasEnoughResources(),
	{ immediate: true },
);

const updateResourcesAndStorage = (): void => {
	throttledUpdateStorageValues();
	updateHasEnoughResources();
};

onMounted((): void => {
	setIconSize();
	updateResourcesAndStorage();
	emitter.on(EmitterEvents.GameTick, updateResourcesAndStorage);
});
onBeforeUnmount((): void => {
	emitter.off(EmitterEvents.GameTick, updateResourcesAndStorage);
});

const handleResourceRepresentation = (): void => {
	if (!hasEnoughResources.value && !statifiedStorageRequirement.value) {
		popupStore.showPopup({
			id: PopupDictionary.NOT_ENOUGH_STORAGE,
			type: PopupType.INFO,
			heading: translate("ui.storage"),
			height: 14,
			isCentered: true,
			isPaddingless: true,
			component: markRaw(NotEnoughStoragePopup),
			componentProps: {
				currency: currency,
				animatedStorageValue: animatedStorageValue.value,
				maxStorageCapacity: maxStorageCapacity.value,
				value: value,
				notEnoughStorage: notEnoughStorage.value,
			},
		});
	}
};

const notEnoughStorage = computed((): string => {
	const translateText =
		currency === ResourceType.ARMY
			? "ui.not_enough_army_storage"
			: "ui.not_enough_storage";
	return replacePlaceholder(
		replacePlaceholder(
			translate(translateText),
			"{material}",
			translate(`resource.${currency}`).toLowerCase(),
		),
		"{icon}",
		`<div class='icon icon-${currency}-16'></div>`,
	);
});

const valueRef = shallowRef<number>(value ?? 0);
const animatedValueRef = useTween(valueRef, {
	easing: linear,
	time: NUMBER_ANIMATION_DURATION,
	precision: 0,
});

watch(
	(): number => value,
	(): void => {
		animatedValueRef.stop();
		animatedValueRef.value = valueRef.value ?? 0;
		valueRef.value = value ?? 0;
	},
);

const storageValueRef = shallowRef<number>(currentStorageCapacity.value ?? 0);
const animatedStorageValue = useTween(storageValueRef, {
	easing: linear,
	time: NUMBER_ANIMATION_DURATION,
	precision: 0,
});

const setIconSize = (): void => {
	const el = document.getElementById("top-arch");
	if (el) {
		const { height } = el.getBoundingClientRect();
		topArchHeight.value = +(height + height / 3).toFixed(2);

		// Calculate closest icon size directly without creating a reactive computed
		const calculateClosestIconSize = (targetHeight: number): number => {
			const closest = iconSizes.reduce(
				(prev: number, curr: number): number => {
					return curr <= targetHeight && curr > prev ? curr : prev;
				},
				0,
			);

			const index = iconSizes.indexOf(closest);
			return iconSizes[index - 1] || closest;
		};

		const closestIconSize = calculateClosestIconSize(topArchHeight.value);

		iconSize.value =
			closestIconSize < iconSizes[0] ? iconSizes[0] : closestIconSize;
	}
};

watch(
	(): number => currentStorageCapacity.value,
	(): void => {
		animatedStorageValue.stop();
		animatedStorageValue.value = storageValueRef.value ?? 0;
		storageValueRef.value = currentStorageCapacity.value ?? 0;
	},
);

watch(
	(): boolean => isSettlementWindowCollapsed.value,
	(): void => {
		if (type === "settlement") {
			setIconSize();
		}
	},
);
</script>

<style lang="scss" scoped>
.main-resource {
	display: flex;
	position: relative;
	flex-direction: column;
	justify-content: center;
	align-items: center;

	#main-resource-bg {
		// It's important to flexibly replicate the element's size
		width: 100%;
		height: 100%;
	}

	&-content {
		display: flex;
		position: absolute;
		bottom: 0;
		flex-direction: column;
		justify-content: center;
		align-items: center;
		margin-top: auto;
		padding-top: 0.25rem;
		padding-bottom: 0.375rem;
		width: 100%;
		height: 100%;
		color: #fff3dd;
		font-size: 1rem;
		line-height: 1rem;

		&__icon {
			flex: 1 1 0;
			min-height: 0;
			overflow: hidden;
		}

		&__value {
			position: absolute;
			bottom: 0.25rem;
			font-size: 0.875rem;
			user-select: none;

			@include md {
				bottom: 0.375rem;
			}
		}
	}

	&--not-enough {
		.main-resource-content {
			&__value {
				color: #ff3d3d;
				&.has-pulse {
					animation: text-pulse 2.5s infinite;
					color: #ff8a8a;
				}
			}
		}
	}

	&__notification {
		position: absolute;
		top: 0.875rem;
		right: -0.188rem;
		z-index: 1;
	}
	&__tooltip {
		&-states {
			@include flex-row-center;
			gap: 0.5rem;
		}
		&-state {
			@include flex-col-center;
			gap: 0.15rem;
			&-head {
				color: #372615;
				font-weight: bold;
				font-size: 0.813rem;
			}
			&-value {
				@include flex-row-center;
				position: relative;
				margin: 0 auto;
				margin-bottom: 0.25rem;
				box-shadow: inset 0 0 0 0.063rem rgba(255, 255, 255, 0.1);
				background-color: #2e2d39;
				padding: 0.25rem 0.5rem;
				width: fit-content;
				min-width: 6.563rem;
				color: #fff3dd;
				font-size: 0.813rem;
				&-red {
					color: #ff3d3d;
				}
				&::before {
					position: absolute;
					top: 0;
					left: 0;
					opacity: 1 !important;
					// NOTE: Multiply blending is better for light colors
					mix-blend-mode: multiply !important;
					background: url($path-images + "backgrounds/textures/marble-texture-bg.avif")
						repeat center;
					background-size: 60%;
					width: 100%;
					height: 100%;
					content: "";
				}
			}
		}
		&-warning {
			@include flex-col-center;
			position: relative;
			box-shadow: inset 0 0 0.75rem 0 rgba(29, 30, 33, 0.8);
			border: 0.063rem solid #cf0032;
			background-color: #401622;
			padding: 0.5rem;
			text-align: center;
			&::before {
				position: absolute;
				top: 0;
				left: 0;
				opacity: 1 !important;
				// NOTE: Multiply blending is better for light colors
				mix-blend-mode: multiply !important;
				background: url($path-images + "backgrounds/textures/marble-texture-bg.avif")
					repeat center;
				background-size: 60%;
				width: 100%;
				height: 100%;
				content: "";
			}
			& > div {
				color: #cf0032;
				font-size: 0.813rem;
			}
			&-head {
				font-weight: bold;
				font-size: 0.906rem;
				text-transform: uppercase;
			}
			&-bold {
				font-weight: bold;
			}
		}
		&-info {
			@include flex-row-center;
			color: #372615;
			font-size: 0.813rem;
			text-align: center;
		}
	}
	:deep(.v-popper) {
		@include flex-row-center;
		width: 100%;
	}
	&.clickable-element {
		cursor: pointer;
		@include clickable-element;
	}
}
@keyframes text-pulse {
	0% {
		color: #ff8a8a;
		text-shadow: 0 0 0.12rem rgba($color: #ff8a8a, $alpha: 0);
	}

	50% {
		color: #ff0a0a;
		text-shadow: 0 0 0.12rem rgba($color: #ff0a0a, $alpha: 1);
	}
	100% {
		color: #ff8a8a;
		text-shadow: 0 0 0.12rem rgba($color: #ff8a8a, $alpha: 0);
	}
}
</style>
